import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, filter, take, Observable } from 'rxjs';
import { AppInfoService } from 'core/app-info/app-info.service';
import { NotificationService } from 'core/notification/notification.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { UtilsService } from 'core/utils/utils.service';
import { BarclaysPaymentService, CaptureContextRequest, ReversePaymentRequest } from '../services/barclays-payment-service';
import { PaymentDetailsService } from '../services/payment-details.service';
import { QuoteDetailsService } from '../services/quote-details.service';

interface BarclaysMessage {
  event: string;
  channel?: string;
  contentType?: string;
  data?: any;
}

interface PaymentStatusResult {
  status: string;
  requestId: string;
  errorReason?: string;
}

@Component({
  selector: 'app-payment-barclays',
  templateUrl: './payment-barclays.component.html',
  styleUrls: ['./payment-barclays.component.scss'],
  standalone: false
})
export class PaymentBarclaysComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('embeddedPaymentContainer', { read: ElementRef }) embeddedPaymentContainer: ElementRef;

  appStrings: AppLocalization;
  quoteId: string;
  quoteNumber: string;
  currencySymbol: string;
  isLoading = true;
  isProcessingPayment = false;
  paymentSummary: any = {};

  private captureContext: string;
  private clientLibraryUrl: string;
  private clientLibraryIntegrity: string;
  private viewInitialized = false;
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private unifiedPayments: any;

  private readonly BARCLAYS_ORIGIN = 'cybersource.com';
  private readonly BARCLAYS_MESSAGE_PREFIX = '/*cybs-telgram*/';

  private readonly PAYMENT_STATUS = {
    AUTHORIZED: 'AUTHORIZED',
    DECLINED: 'DECLINED',
    AUTHORIZED_PENDING_REVIEW: 'AUTHORIZED_PENDING_REVIEW',
    PARTIAL_AUTHORIZED: 'PARTIAL_AUTHORIZED',
    INVALID_REQUEST: 'INVALID_REQUEST',
    UNKNOWN: 'UNKNOWN'
  };

  private readonly ERROR_REASON = {
    CV_FAILED: 'CV_FAILED'
  };

  private readonly BARCLAYS_PAYMENT_DECLINED_STATUSES = [
    this.PAYMENT_STATUS.DECLINED,
    this.PAYMENT_STATUS.AUTHORIZED_PENDING_REVIEW,
    this.PAYMENT_STATUS.INVALID_REQUEST,
    this.PAYMENT_STATUS.PARTIAL_AUTHORIZED
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private barclaysPaymentService: BarclaysPaymentService,
    private paymentDetailsService: PaymentDetailsService,
    private quoteDetailsService: QuoteDetailsService,
    private appInfoService: AppInfoService,
    private notificationService: NotificationService,
    private utilsService: UtilsService
  ) { }

  /**
   * Initializes the component by loading quote ID and payment data.
   * Called once after the component is created.
   */
  ngOnInit(): void {
    this.appStrings = this.appInfoService.appStrings;
    this.currencySymbol = this.appInfoService.currencySymbol;
    this.initializeQuoteId();
    if (!this.quoteId) {
      return;
    }
    this.initializePaymentData();
  }

  /**
   * Initializes the payment SDK after the view is fully rendered.
   * Sets the viewInitialized flag and loads SDK if capture context is ready.
   */
  ngAfterViewInit(): void {
    this.viewInitialized = true;

    if (this.isReadyToInitializeSDK()) {
      this.initializePaymentSDK();
    }
  }

  /**
   * Cleanup lifecycle hook that removes the message listener when component is destroyed.
   */
  ngOnDestroy(): void {
    this.cleanupMessageListener();
  }

  /**
   * Extracts and validates the quote ID from the route parameters.
   * Displays an error if quote ID is missing.
   */
  private initializeQuoteId(): void {
    this.quoteId = this.route.snapshot.paramMap.get('quoteId');
    if (!this.quoteId) {
      this.navigateToQuote(this.appStrings.barclays_quoteIdMissing);
    }
  }

  /**
   * Loads payment summary and quote details, then initiates the capture context request.
   * Combines multiple observables to gather all required payment data.
   */
  private initializePaymentData(): void {
    this.paymentDetailsService.getPaymentSummary(this.quoteId, () => { });

    combineLatest([
      this.getQuoteDetailsObservable(),
      this.paymentDetailsService.paymentSummary$.pipe(
        filter(summary => summary?.grandTotal !== undefined),
        take(1)
      )
    ]).subscribe(([quoteDetails, paymentSummary]) => {
      this.quoteNumber = quoteDetails.quoteNumber;
      this.paymentSummary = paymentSummary;
      this.loadCaptureContext();
    });
  }

  /**
   * Wraps the quote details service callback in an Observable for use with combineLatest.
   * @returns Observable that emits quote details
   */
  private getQuoteDetailsObservable(): Observable<any> {
    return new Observable((observer) => {
      this.quoteDetailsService.getQuoteDetails(this.quoteId, (quoteDetails) => {
        observer.next(quoteDetails);
        observer.complete();
      });
    });
  }

  /**
   * Checks if all required data is available to initialize the Barclays payment SDK.
   * @returns true if capture context, client library URL, and integrity hash are all present
   */
  private isReadyToInitializeSDK(): boolean {
    return !!(this.captureContext && this.clientLibraryUrl && this.clientLibraryIntegrity);
  }

  /**
   * Requests the capture context from Barclays payment service.
   * The capture context contains the JWT token needed to initialize the payment form.
   */
  private loadCaptureContext(): void {
    const grandTotal = this.paymentSummary?.grandTotal || 0;
    const request: CaptureContextRequest = {
      totalAmount: grandTotal.toString(),
      clientReferenceInformationCode: this.quoteNumber
    };

    this.barclaysPaymentService.getCaptureContext(request).subscribe({
      next: (response: any) => this.handleCaptureContextSuccess(response),
      error: (err) => this.handleCaptureContextError(err)
    });
  }

  /**
   * Processes successful capture context response from Barclays.
   * Extracts JWT token, parses client library data, and initializes payment SDK if view is ready.
   * @param response - The capture context response containing JWT token
   */
  private handleCaptureContextSuccess(response: any): void {
    if (!response) {
      this.navigateToQuote(this.appStrings.barclays_contextResponseEmpty);
      return;
    }

    const jwtToken = typeof response === 'string' ? response : response.captureContext;
    if (!jwtToken) {
      this.navigateToQuote(this.appStrings.barclays_jwtTokenMissing);
      return;
    }

    this.captureContext = jwtToken;
    this.extractClientLibraryData(jwtToken);
    this.isLoading = false;

    if (this.viewInitialized) {
      this.initializePaymentSDK();
    }
  }

  /**
   * Decodes the JWT token and extracts Barclays client library URL and integrity hash.
   * These values are required to load the payment SDK script securely.
   * @param jwtToken - JWT token from capture context response
   * @throws Error if JWT decoding fails or required data is missing
   */
  private extractClientLibraryData(jwtToken: string): void {
    const decodedPayload = this.utilsService.decodeJWT(jwtToken);
    if (decodedPayload?.ctx && Array.isArray(decodedPayload.ctx) && decodedPayload.ctx[0]) {
      const ctxData = decodedPayload.ctx[0].data;
      this.clientLibraryUrl = ctxData.clientLibrary;
      this.clientLibraryIntegrity = ctxData.clientLibraryIntegrity;
    } else {
      throw new Error(this.appStrings.barclays_jwtDecodeFailed);
    }
  }

  /**
   * Handles errors that occur during capture context loading.
   * Maps error to user-friendly message and displays error.
   * @param err - Error object from HTTP request failure
   */
  private handleCaptureContextError(err: any): void {
    this.isLoading = false;

    const errorMessage = this.getErrorMessage(err);
    this.navigateToQuote(errorMessage);
  }

  /**
   * Maps HTTP error responses to user-friendly error messages.
   * Handles timeout, connection, and server errors with appropriate messages.
   * @param err - Error object from HTTP request
   * @returns Localized error message string
   */
  private getErrorMessage(err: any): string {
    if (err.message?.includes('timed out')) {
      return this.appStrings.barclays_requestTimeout;
    }
    if (err.status === 0) {
      return this.appStrings.barclays_connectionError;
    }
    if (err.status >= 500) {
      return this.appStrings.barclays_serviceUnavailable;
    }
    return this.appStrings.barclays_contextLoadFailed;
  }

  /**
   * Initializes the Barclays payment SDK by injecting the client library script.
   * Creates script element with integrity check and loads payment form on success.
   */
  private initializePaymentSDK(): void {
    if (!this.isReadyToInitializeSDK()) {
      this.navigateToQuote(this.appStrings.barclays_contextNotLoaded);
      return;
    }

    const script = document.createElement('script');
    script.src = this.clientLibraryUrl;
    script.integrity = this.clientLibraryIntegrity;
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.onload = () => this.showPaymentForm(this.captureContext);
    script.onerror = () => this.navigateToQuote(this.appStrings.barclays_sdkLoadFailed);

    document.head.appendChild(script);
  }

  /**
   * Displays the embedded Barclays payment form using the Unified Payments API.
   * Sets up message listener and creates payment trigger for the embedded container.
   * @param captureContext - JWT token for initializing payment form
   */
  private async showPaymentForm(captureContext: string): Promise<void> {
    try {
      const acceptFn = (window as any).Accept;
      if (!acceptFn) {
        throw new Error(this.appStrings.barclays_acceptFunctionNotFound);
      }

      const accept = await acceptFn(captureContext);
      this.unifiedPayments = await accept.unifiedPayments(false);

      this.setupBarclaysMessageListener();

      const trigger = this.unifiedPayments.createTrigger('PANENTRY', {
        containers: { paymentScreen: '#embeddedPaymentContainer' }
      });

      this.handleTriggerResponse(trigger);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : this.appStrings.barclays_displayFormError;
      this.navigateToQuote(errorMessage);
    }
  }

  /**
   * Sets up a message listener to handle Barclays iframe communication.
   * Listens for CLOSE events to detect when user cancels payment.
   */
  private setupBarclaysMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {
      if (!this.isBarclaysMessage(event)) {
        return;
      }

      const message = this.parseBarclaysMessage(event.data);
      if (message?.event === 'CLOSE') {
        this.handleUserCancellation();
      }
    };

    window.addEventListener('message', this.messageListener);
  }

  /**
   * Validates that a postMessage event is from Barclays and properly formatted.
   * Checks origin and message prefix to prevent processing unrelated messages.
   * @param event - MessageEvent from window.postMessage
   * @returns true if message is from Barclays with correct format
   */
  private isBarclaysMessage(event: MessageEvent): boolean {
    return event.origin.includes(this.BARCLAYS_ORIGIN) &&
      typeof event.data === 'string' &&
      event.data.startsWith(this.BARCLAYS_MESSAGE_PREFIX);
  }

  /**
   * Parses a Barclays message by removing prefix and parsing JSON payload.
   * @param data - Raw message string from Barclays iframe
   * @returns Parsed BarclaysMessage object or null if parsing fails
   */
  private parseBarclaysMessage(data: string): BarclaysMessage | null {
    try {
      const jsonStr = data.substring(this.BARCLAYS_MESSAGE_PREFIX.length);
      return JSON.parse(jsonStr);
    } catch (e) {
      return null;
    }
  }

  /**
   * Handles user canceling the payment by cleaning up and navigating back to quote.
   */
  private handleUserCancellation(): void {
    this.cleanupMessageListener();
    this.navigateToQuote();
  }

  /**
   * Handles the payment form trigger response after user interaction.
   * Processes successful token generation or handles cancellation/error.
   * @param trigger - Barclays payment trigger object with show() method
   */
  private handleTriggerResponse(trigger: any): void {
    trigger.show()
      .then((transientToken: any) => {
        this.cleanupMessageListener();
        if (transientToken) {
          this.completePayment(transientToken);
        } else {
          this.navigateToQuote();
        }
      })
      .catch((error: any) => {
        this.cleanupMessageListener();
        const isUserCancellation = error?.message?.includes('cancel') || error?.message?.includes('back');
        if (isUserCancellation) {
          this.navigateToQuote();
        } else {
          this.navigateToQuote(`${this.appStrings.barclays_formError}: ${error?.message || 'Unknown error'}`);
        }
      });
  }

  /**
   * Removes the Barclays message event listener to prevent memory leaks.
   */
  private cleanupMessageListener(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
  }

  /**
   * Completes the payment by first calling up.complete() then authorizing with backend.
   * First calls Barclays SDK complete() to get payment token, then calls backend authorization API.
   * @param transientToken - JWT token generated from payment form submission
   */
  private async completePayment(transientToken: string): Promise<void> {
    this.isProcessingPayment = true;

    try {
      // Call the complete method on the Unified Payments instance to authorize the payment and get the final token
      const completeResponse = await this.unifiedPayments.complete(transientToken);

      const { status, requestId, errorReason } = this.getPaymentStatus(completeResponse);
      if (status === this.PAYMENT_STATUS.AUTHORIZED_PENDING_REVIEW && errorReason === this.ERROR_REASON.CV_FAILED) {
        this.reverseAndDeclinePayment(status, requestId);
        return;
      }
      if (status !== this.PAYMENT_STATUS.AUTHORIZED) {
        this.handlePaymentDeclined(status);
        return;
      }

      // After successful completion, call the backend API to send payment details to CCM
      this.barclaysPaymentService.completePayment({ transientTokenJwt: completeResponse }).subscribe({
        next: () => this.handlePaymentAuthorizationSuccess(),
        error: () => this.handlePaymentAuthorizationError()
      });
    } catch (error) {
      this.handlePaymentAuthorizationError();
    }
  }

  /**
   * Extracts payment status and request ID from Barclays complete response.
   * @param response - Response from unifiedPayments.complete()
   * @returns Object containing status and requestId
   */
  private getPaymentStatus(response: any): PaymentStatusResult {
    try {
      const decoded = this.utilsService.decodeJWT(response);
      return {
        status: decoded?.status || this.PAYMENT_STATUS.UNKNOWN,
        requestId: decoded?.id || '',
        errorReason: decoded?.details?.errorInformation?.reason
      };
    } catch {
      if (typeof response === 'object' && response) {
        return {
          status: response.status || this.PAYMENT_STATUS.UNKNOWN,
          requestId: '',
          errorReason: response.details?.errorInformation?.reason
        };
      }
      return { status: this.PAYMENT_STATUS.UNKNOWN, requestId: '' };
    }
  }

  /**
   * Reverses an AUTHORIZED_PENDING_REVIEW payment and then follows the declined flow.
   */
  private reverseAndDeclinePayment(status: string, requestId: string): void {
    const request: ReversePaymentRequest = {
      requestId,
      clientReferenceInformationCode: this.quoteNumber,
      totalAmount: (this.paymentSummary?.grandTotal || 0).toString()
    };

    this.barclaysPaymentService.reversePayment(request).subscribe({
      next: () => this.handlePaymentDeclined(status),
      error: () => this.handlePaymentDeclined(status)
    });
  }

  /**
   * Handles declined or pending payment status.
   * Stops payment processing and navigates back to quote with appropriate error message.
   * @param statusInfo - Payment status from Barclays response
   */
  private handlePaymentDeclined(status: string): void {
    this.isProcessingPayment = false;

    let errorMessage: string;
    if (this.BARCLAYS_PAYMENT_DECLINED_STATUSES.includes(status)) {
      errorMessage = this.appStrings.barclays_paymentDeclined;
    } else {
      errorMessage = this.appStrings.barclays_processingFailed;
    }

    this.navigateToQuote(errorMessage);
  }

  /**
   * Handles successful payment authorization response.
   * If we reach this method, the HTTP call returned 200, meaning payment was authorized.
   * The API returns "0" for success, so we don't check response content.
   * Completes the order via API and navigates to quote page with order details.
   */
  private handlePaymentAuthorizationSuccess(): void {
    this.completeOrderAfterPayment();
  }

  /**
   * Completes the order after successful payment authorization.
   * Calls the complete order API and navigates to quote page with order details.
   */
  private completeOrderAfterPayment(): void {
    this.quoteDetailsService.completeOrder(this.quoteId).subscribe({
      next: (order) => {
        this.notificationService.showNotification(
          this.appStrings.barclays_paymentSuccessful,
          NotificationType.Success
        );
        this.router.navigate(['/quotes', this.quoteId], {
          state: {
            orderCompleted: true,
            orderConfirmationNumber: order.quoteNumber,
            orderId: order.quoteId,
            quoteRevisionId: order.quoteRevisionId
          }
        });
      },
      error: () => {
        this.isProcessingPayment = false;
        this.navigateToQuote(this.appStrings.barclays_orderCompletionFailed);
      }
    });
  }

  /**
   * Handles payment authorization errors by stopping processing and displaying error.
   */
  private handlePaymentAuthorizationError(): void {
    this.isProcessingPayment = false;
    this.navigateToQuote(this.appStrings.barclays_processingFailed);
  }

  /**
   * Navigates back to the quote page, optionally passing error message via router state.
   * @param errorMessage - Optional error message to display on quote page
   */
  private navigateToQuote(errorMessage?: string): void {
    if (errorMessage) {
      this.router.navigate(['/quotes', this.quoteId], {
        state: { paymentError: errorMessage }
      });
    } else {
      this.router.navigate(['/quotes', this.quoteId]);
    }
  }
}

